import { useCallback, useEffect, useRef } from 'react';
import moment from 'moment/moment';

import { formatPeriodDate, getFiltersWithoutSegment } from '../../../helpers/utils/functions';
import { showErrorMessage, showSuccessMessage } from '../../../helpers/utils/ui';
import { useActions } from './useActions';
import { useTypedSelector } from './useTypedSelector';
import { Roles } from '../constants/user';

interface UseSaveDashboardShortcutOptions {
  /**
   * Включить/выключить обработку горячих клавиш
   */
  enabled?: boolean;
  /**
   * Текущий масштаб дашборда (для сохранения)
   */
  scale?: number;
  /**
   * Callback при невозможности сохранить (новый дашборд, нет прав).
   * Используется для открытия модального окна "Сохранить как".
   */
  onSaveAs?: () => void;
}

/**
 * Хук для сохранения дашборда по Ctrl+S (Windows/Linux) или Cmd+S (macOS)
 * 
 * @param options - опции хука
 * @returns объект с функцией save для ручного вызова
 */
export const useSaveDashboardShortcut = (options: UseSaveDashboardShortcutOptions = {}) => {
  const { enabled = true, scale, onSaveAs } = options;
  
  const { role } = useTypedSelector((state) => state.currentUser);
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { savedDashboards, currentDashboard, isLoadingReports } = useTypedSelector(
    (state) => state.reports
  );
  const period = useTypedSelector((state) => state.period);
  const { dashboards } = useTypedSelector((state) => state.dashboard);
  const { detailsDashboard } = useTypedSelector(
    (state) => state.details
  );
  const { filters, currentSegment } = useTypedSelector((state) => state.filter);
  const { attributesDashboard } = useTypedSelector(
    (state) => state.attributes
  );
  const { isCompare } = useTypedSelector((state) => state.meta);
  const tableBlocks = useTypedSelector((state) => state.table.blocks);

  const { putReport } = useActions();

  // Ref для предотвращения множественных сохранений
  const isSavingRef = useRef(false);

  // Проверяем, можно ли сохранять (есть права на редактирование)
  const canSave = useCallback(() => {
    // Нет текущего дашборда
    if (Object.keys(currentDashboard).length === 0) {
      return false;
    }

    // Режим сравнения
    if (isCompare) {
      return false;
    }

    // Нет блоков
    if (!dashboards || dashboards.length === 0) {
      return false;
    }

    // Проверяем права доступа
    const dashboard = savedDashboards.find(
      (d) => d?.hash === currentDashboard?.hash || d?.id === currentDashboard?.id
    );
    if (dashboard && dashboard.access_id > Roles[role]) {
      return false;
    }

    return true;
  }, [currentDashboard, isCompare, dashboards, savedDashboards, role]);

  // Функция сохранения дашборда
  const save = useCallback(() => {
    if (isSavingRef.current) {
      return;
    }

    if (!canSave()) {
      // Если режим сравнения — "Сохранить как" тоже неуместен
      if (isCompare) {
        showErrorMessage('Сохранение недоступно в режиме сравнения');
        return;
      }

      // Новый дашборд, нет прав или нет блоков — открываем "Сохранить как"
      if (onSaveAs) {
        onSaveAs();
      } else {
        if (Object.keys(currentDashboard).length === 0) {
          showErrorMessage('Сначала откройте или создайте дашборд');
        } else if (!dashboards || dashboards.length === 0) {
          showErrorMessage('Добавьте блоки перед сохранением');
        } else {
          showErrorMessage('Недостаточно прав для сохранения этого дашборда');
        }
      }
      return;
    }

    if (!selectedSchool?.id) {
      showErrorMessage('Школа не выбрана');
      return;
    }

    isSavingRef.current = true;

    // Мержим runtime данные таблиц (sortTable, columnWidth, columnsWidth) в блоки
    const mergedBlocks = dashboards.map((block: any) => {
      const blockId = block?.data?.blockId;
      const tableBlockState = blockId && tableBlocks ? tableBlocks[blockId] : null;
      if (!tableBlockState) return block;

      return {
        ...block,
        data: {
          ...block.data,
          ...(tableBlockState.sortTable !== undefined && { sortTable: tableBlockState.sortTable }),
          ...(tableBlockState.columnWidth !== undefined && { columnWidth: tableBlockState.columnWidth }),
          ...(tableBlockState.columnsWidth !== undefined && { columnsWidth: tableBlockState.columnsWidth }),
          // Колонки со спарклайном, включённые прямо в блоке
          ...(tableBlockState.sparklines !== undefined && { sparklines: tableBlockState.sparklines }),
          // Заливка ячеек обычных метрик, настроенная прямо в блоке
          ...(tableBlockState.cellFills !== undefined && { cellFills: tableBlockState.cellFills }),
          // «Динамика» и заливка всей таблицы, включённые в окне блока
          ...(tableBlockState.analysis !== undefined && { analysis: tableBlockState.analysis }),
          ...(tableBlockState.cellFilling !== undefined && { cellFilling: tableBlockState.cellFilling }),
        },
      };
    });

    // Формируем данные для сохранения (аналогично UniversalReportTitle.save)
    let values: any = {
      ...currentDashboard.values,
      period: {
        start: formatPeriodDate(period.start, period.withTime),
        end: formatPeriodDate(period.end, period.withTime),
        interval: period.interval || period.select,
        select: period.select || period.interval,
        days: null,
      },
      attributesDashboard,
      detailsDashboard,
      filters,
      segment: null,
      blocks: mergedBlocks,
      zoom: scale ?? 1,
    };

    // Обработка сегмента
    if (Object.keys(currentSegment || {}).length > 0) {
      values.segment = currentSegment;
      values.filters = getFiltersWithoutSegment(
        filters,
        currentSegment?.values?.filters
      );
    } else {
      values.segment = {};
      values.filters = filters;
    }

    // Обработка периода "N дней назад"
    if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
      values.period.days = period.days;
    }

    // Вызываем сохранение
    putReport(selectedSchool.id, { id: currentDashboard.id, values });

    // Сбрасываем флаг через небольшую задержку
    setTimeout(() => {
      isSavingRef.current = false;
    }, 1000);
  }, [
    canSave,
    onSaveAs,
    currentDashboard,
    isCompare,
    dashboards,
    selectedSchool,
    period,
    attributesDashboard,
    detailsDashboard,
    filters,
    currentSegment,
    putReport,
    scale,
    tableBlocks,
  ]);

  // Обработчик клавиатуры
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S (Windows/Linux) или Cmd+S (macOS)
      const isSaveShortcut = (e.ctrlKey || e.metaKey) && e.key === 's';
      
      if (isSaveShortcut) {
        // Предотвращаем стандартное поведение браузера (сохранение страницы)
        e.preventDefault();
        e.stopPropagation();
        
        save();
      }
    };

    // Используем capture: true чтобы перехватить событие раньше других обработчиков
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [enabled, save]);

  return {
    save,
    canSave: canSave(),
    isLoading: isLoadingReports,
  };
};

export default useSaveDashboardShortcut;
